import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import ColorPicker from "../../../../../components/ColorPicker";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";

import { showToastOnApiCall, getXtenantId } from "../../../../../utils/utils";
import * as Yup from "yup";
import Input from "../../../../../components/Input";
import { useAddFlag } from "../../../../../queries/flags/flags/use-add-flag";
import { useUpdateFlag } from "../../../../../queries/flags/flags/use-update-flag";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import {
  FLAG_COLOR_INVALID_MESSAGE,
  FLAG_COLOR_REQUIRED_MESSAGE,
  FLAG_NAME_REQUIRED_MESSAGE,
  HEX_COLOR_REGEX,
} from "./flag-setting-constant";
import { FlagFormValues } from "./flag-setting-types";
const UpsertFlag = ({ disclosureHost }: { disclosureHost: DisclosureHost }) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updateFlag } = useUpdateFlag();
  const { mutateAsync: addFlag } = useAddFlag();

  const formik = useFormik<FlagFormValues>({
    initialValues: disclosureHost.data
      ? {
          uuid: disclosureHost.data?.uuid,
          name: disclosureHost.data?.name ?? "",
          color: disclosureHost.data?.color,
        }
      : {
          name: "",
          color: undefined,
        },
    validationSchema: Yup.object({
      name: Yup.string().required(FLAG_NAME_REQUIRED_MESSAGE),
      color: Yup.string()
        .required(FLAG_COLOR_REQUIRED_MESSAGE)
        .matches(HEX_COLOR_REGEX, FLAG_COLOR_INVALID_MESSAGE),
    }),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  async function handleSubmit(values: FlagFormValues) {
    const payload = {
      flags: {
        uuid: values.uuid || undefined,
        name: values.name,
        color: values.color,
      },
      xTENANTID: getXtenantId(),
    };

    if (disclosureHost.data) showToastAndResetForm(updateFlag(payload));
    else showToastAndResetForm(addFlag(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<unknown>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["flag-list"],
        });
        closeAndResetForm();
      },
    });
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      title={disclosureHost.data ? "Edit Patient Flag" : "Add New Patient Flag"}
      w="900px"
      footerActions={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={disclosureHost.close}
            sx={{ mr: 2 }}
          />
          <Button variant="contained" onClick={formik.submitForm}>
            {disclosureHost.data ? "Update Flag" : "Create Flag"}
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} gap={2}>
          {/* Flag Name */}
          <Grid item xs={12}>
            <Input
              required
              label="Flag Name"
              error={formik.touched.name && (formik.errors.name as string)}
              textFieldProps={{
                fullWidth: true,
                name: "name",
                value: formik.values?.name,
                onChange: formik.handleChange,
                placeholder: "Enter Name",
              }}
            />
          </Grid>

          {/* Flag Color */}
          <Grid item xs={12}>
            <ColorPicker
              required
              label="Color"
              value={formik.values?.color}
              onChange={(color) => {
                formik.setFieldTouched("color", true, false);
                formik.setFieldValue("color", color);
              }}
              error={formik.touched.color && (formik.errors.color as string)}
              placeholder="Select flag color"
            />
          </Grid>
        </Grid>
      </form>
    </SideSheetDrawer>
  );
};

export default UpsertFlag;
